import { atom } from 'nanostores';
import type { WritableAtom } from 'nanostores';

const PREFIX = 'logbook-';

/**
 * Creates a persistent atom that syncs with localStorage
 */
export function persistentAtom<T>(
  key: string,
  initialValue: T,
): WritableAtom<T> {
  const store = atom<T>(initialValue);

  // Load from localStorage on creation
  if (typeof window !== 'undefined') {
    try {
      const item = window.localStorage.getItem(PREFIX + key);
      if (item) {
        store.set(JSON.parse(item) as T);
      }
    } catch (error) {
      console.warn(`Error reading localStorage key "${PREFIX + key}":`, error);
    }
  }

  // Subscribe to changes and persist
  store.subscribe(value => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(PREFIX + key, JSON.stringify(value));
      } catch (error) {
        console.warn(
          `Error setting localStorage key "${PREFIX + key}":`,
          error,
        );
      }
    }
  });

  // Listen for changes from other tabs
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', event => {
      if (event.key === PREFIX + key && event.newValue) {
        try {
          store.set(JSON.parse(event.newValue) as T);
        } catch (error) {
          console.warn(
            `Error parsing localStorage key "${PREFIX + key}" on storage event:`,
            error,
          );
        }
      }
    });
  }

  return store;
}

/**
 * Helper to get localStorage value synchronously (for initialization)
 */
export function getStoredValue<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') {
    return defaultValue;
  }
  try {
    const item = window.localStorage.getItem(PREFIX + key);
    return item ? (JSON.parse(item) as T) : defaultValue;
  } catch (error) {
    console.warn(`Error reading localStorage key "${PREFIX + key}":`, error);
    return defaultValue;
  }
}
